import { useDispatch, useSelector } from "react-redux";
import { mainActions } from "../../store";
import RefineDialog from "./RefineDialog/RefineDialog";
import classes from "./RefineScalePage.module.css";
import RefinePageDialog from "./RefinePageDialog/RefinePageDialog";
import BottomMenu from "../BottomMenu/BottomMenu";
import Card from "../UI/Card";
import RequestSupportCard from "../UI/RequestSupportCard";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ChartWorkflow, WorkflowActions } from "../ChartWorkflow";
import ChartSaveToolbar from "../MyChartsPage/ChartSaveToolbar";
import RefineHistoryToolbar from "./RefineHistoryToolbar";
import RefineQuickTour from "./RefineQuickTour";

const RefineScalePage = () => {
  const publishedData = useSelector((state) => state.publishedData);
  const cards = useSelector(state => state.chartHistory.cards);
  const dispatch = useDispatch();
  //console.log(publishedData);

  let maxImageWidth = 0;

  let liveIndex = 0;
  const elements = cards.map((card, slot) => {
    if (card.deleted) return <div key={card.id} className={classes.restoreCard} role="group" aria-label={`Chart ${slot + 1}`}>
      <p>Chart {slot + 1} deleted</p>
      <button type="button" onClick={() => dispatch(mainActions.restoreChart(card.id))}>Restore</button>
    </div>;
    const index = liveIndex++;
    const perChart = publishedData[index];
    if (!perChart) return null;
    const src = `data:image/svg+xml;utf8,${encodeURIComponent(perChart.image)}`;
    if (perChart.width > maxImageWidth) {
      maxImageWidth = perChart.width;
    }
    return (
      <div key={card.id}>
        <RefineDialog
          src={src}
          index={index}
          width={perChart.width}
          height={perChart.height}
          chartData={perChart.chartData}
          fullChartData={perChart.fullChartData}
          noteIds={perChart.noteIds}
          frets={perChart.fretCount}
          history={card}
          number={slot + 1}
        />
      </div>
    );
  });

  maxImageWidth += 79;

  return (
    <div className={classes.background}>
      <HelmetProvider>
        <Helmet>
          <meta charset="UTF-8" />
          <title>Guitar Charts Designer</title>
          <meta
            name="description"
            content="Guitar Charts Designer is a web app that lets you create and print custom guitar charts and diagrams."
          />
        </Helmet>
      </HelmetProvider>

      <header className={classes.editorHeader}>
        <div className={classes.heading}>
          <h1 className={classes.h1}>Guitar Charts Designer - Refine</h1>
        </div>
        <ChartWorkflow step={2} />
        <div className={classes.helpLinks}>
          <a href="#usage">How to use</a>
          <RefineQuickTour />
        </div>
      </header>
      <section className={classes.pageDetails} aria-label="Page details">
        <h2 className={classes.detailsHeading}>Page details</h2>
        <RefinePageDialog />
        <RefineHistoryToolbar showHeading={false}>
          <ChartSaveToolbar compact allowCopy={false} />
        </RefineHistoryToolbar>
      </section>
      <Card>
        <div
          className={classes.imageGrid}
          style={{
            //gridTemplateColumns: `repeat(auto-fit, minmax(${maxImageWidth}px, 1fr))`,
            justifyItems: "center",
            alignItems: "center",
          }}
        >
          {elements}
        </div>
      </Card>
      <WorkflowActions step={2} />
      <Card>
        <div className={classes.howToUse}>
          <h3 id="usage">How To Use</h3>
          <ol>
            <li>
              Each chart has its own <b>Undo</b> and <b>Redo</b> controls.
              Use <b>Restore</b> in a deleted chart's place to bring it back.
              Page details have separate undo and redo controls.
              History is kept while previewing, and cleared when you return to Create,
              load another chart, or reload the page.
            </li>
            <li>
              Give your Page a <b>Title</b>
            </li>
            <li>
              Add any <b>Introduction</b> text you choose using the{" "}
              <a
                href="https://commonmark.org/help/"
                target="_blank"
                rel="noreferrer"
              >
                Markdown for formatting
              </a>
              .
            </li>
            <li>
              Drag an outlined end note to move it to an adjacent string, or
              focus it and use the up/down arrow keys. Choose <b>Remove notes</b>
              on a chart to remove individual notes without shifting the others.
            </li>
            <li>
              <b>Delete</b> or <b>Duplicate</b> Charts using their buttons.
            </li>
            <li>
              <b>Preview</b> the final Chart.
            </li>
            <li>
              <b>Publish</b> to create a Bookmark-able, Linkable, Printable Page
              that you can save and use as you want.
            </li>
          </ol>
        </div>
      </Card>
      <RequestSupportCard />
      <Card>
        <BottomMenu />
      </Card>
    </div>
  );
};

export default RefineScalePage;
